// The rider's pose targets and the springs that chase them. Render-only and
// three.js-free: `SkaterModel` picks a named target per state, adds the
// moment's offsets (balance, sway, spin), and each joint axis eases toward it
// through its own critically damped spring, so mode changes, landings and
// grabs flow instead of snapping. New poses (per-grab poses, other skaters)
// slot in as entries here.

/** One arm's free pose, radians. */
export interface ArmTarget {
  /** Out from the body along the board (the arm's own side), 0 hanging straight down. */
  out: number;
  /** Swung back behind the chest (+) or forward in front of it (-). */
  back: number;
  /** Elbow bend, 0 straight. */
  bend: number;
}

export interface PoseTarget {
  front: ArmTarget;
  back: ArmTarget;
  /** How far the hips sit below standing tall, metres: soft knees. */
  kneeSoft: number;
}

export type PoseName = "roll" | "balance" | "air" | "bail" | "skitch" | "walk" | "hang";

/**
 * Relaxed rolling: arms hang 20-35 degrees out, loose elbows, soft knees.
 * They go up only for a reason: balancing on a rail or a manual, the air,
 * a slam.
 */
export const POSES: Record<PoseName, PoseTarget> = {
  roll: { front: { out: 0.42, back: 0.12, bend: 0.5 }, back: { out: 0.42, back: 0.18, bend: 0.55 }, kneeSoft: 0.018 },
  balance: { front: { out: 0.85, back: 0.2, bend: 0.8 }, back: { out: 0.85, back: 0.2, bend: 0.8 }, kneeSoft: 0.03 },
  air: { front: { out: 0.75, back: 0.3, bend: 0.65 }, back: { out: 0.7, back: 0.35, bend: 0.7 }, kneeSoft: 0 },
  bail: { front: { out: 2.4, back: 0, bend: 0.7 }, back: { out: 2.4, back: 0, bend: 0.7 }, kneeSoft: 0 },
  // Towed behind a vehicle (#37): front arm straight out ahead, holding its back end; the other out for balance.
  skitch: { front: { out: 0.55, back: -1.25, bend: 0.12 }, back: { out: 0.9, back: 0.25, bend: 0.6 }, kneeSoft: 0.08 },
  // On foot (#19): the front arm hangs loose to swing with the stride, the back one holds the board against the hip.
  walk: { front: { out: 0.12, back: 0, bend: 0.3 }, back: { out: 0.35, back: 0.15, bend: 1.3 }, kneeSoft: 0.02 },
  // Hanging off a ledge on foot (#19): the free arm straight up holding the edge, the board still under the other.
  hang: { front: { out: 2.5, back: -0.5, bend: 0.1 }, back: { out: 0.35, back: 0.15, bend: 1.3 }, kneeSoft: 0 },
};

/** Joint limits, radians. Elbows and knees bend one way only. */
export const LIMITS = {
  elbow: (150 * Math.PI) / 180,
  knee: (150 * Math.PI) / 180,
  out: [-0.15, 2.6] as const,
  back: [-1.2, 1.2] as const,
};

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * A critically damped spring on one number: it heads for its target as fast
 * as it can without overshooting. `tau` is the time to cover about 63% of a
 * step. The update is the exact solution, so it's stable at any dt.
 */
export class Spring {
  vel = 0;
  private omega: number;

  constructor(
    public value: number,
    tau: number,
  ) {
    this.omega = 2.15 / tau;
  }

  update(target: number, dt: number): number {
    const w = this.omega;
    const x = this.value - target;
    const t = (this.vel + w * x) * dt;
    const e = Math.exp(-w * dt);
    this.value = target + (x + t) * e;
    this.vel = (this.vel - w * t) * e;
    return this.value;
  }

  /** Jumps straight to `v` at rest (a pose that's set outright, like a slam). */
  set(v: number): void {
    this.value = v;
    this.vel = 0;
  }
}

/** The three springs behind one arm's free pose. */
export class ArmSprings {
  out: Spring;
  back: Spring;
  bend: Spring;

  constructor(start: ArmTarget, tau = 0.12) {
    this.out = new Spring(start.out, tau);
    this.back = new Spring(start.back, tau);
    this.bend = new Spring(start.bend, tau * 0.8);
  }

  update(t: ArmTarget, dt: number): ArmTarget {
    return {
      out: clamp(this.out.update(t.out, dt), ...LIMITS.out),
      back: clamp(this.back.update(t.back, dt), ...LIMITS.back),
      bend: clamp(this.bend.update(t.bend, dt), 0, LIMITS.elbow),
    };
  }

  set(t: ArmTarget): void {
    this.out.set(t.out);
    this.back.set(t.back);
    this.bend.set(t.bend);
  }
}
